import { useCallback, useRef, useState } from 'react'
import mapboxgl, { Map } from 'mapbox-gl'
import { Position } from 'geojson'
import { useAtomValue } from 'jotai'
import { BaseMap } from '@app/components/BaseMap/BaseMap'
import { MapConfig } from '@app/components/BaseMap/config'
import { clientLocationAtom } from '@app/state/clientsAtom'
import { DocumentUploadFormData } from '../../../documents-upload.types'
import { useDrawPolygon } from './use-draw-polygon'
import { useMarkerSync, POINT_ZOOM_LEVEL } from './use-marker-sync'
import { usePolygonPreview } from './use-polygon-preview'
import { useMapGeocoder } from './use-map-geocoder'
import { GeoreferenceHeader } from './georeference-header'
import { GeoreferenceStatusBar } from './georeference-status-bar'

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN
mapboxgl.accessToken = MAPBOX_TOKEN

// Plain controlled value/onChange rather than react-hook-form — shared between Upload and Edit's differently-shaped forms.
export type GeoreferenceValue = DocumentUploadFormData['georeference']

type GeoreferenceStepProps = {
  value: GeoreferenceValue
  onChange: (value: GeoreferenceValue) => void
}

export type ActiveTool = 'pin' | 'draw'

export const GeoreferenceStep = ({ value: georeference, onChange }: GeoreferenceStepProps) => {
  const [map, setMap] = useState<Map | null>(null)
  const geocoderContainerRef = useRef<HTMLDivElement>(null)
  // Falls back to the whole-of-NZ default when no client is configured.
  const clientLocation = useAtomValue(clientLocationAtom)
  // Separate from georeference.type: a fresh Draw session starts with type still NONE.
  const [activeTool, setActiveTool] = useState<ActiveTool>(
    georeference.type === 'POLYGON' ? 'draw' : 'pin',
  )
  // Bumped by Clear to wipe an in-progress polygon — see useDrawPolygon.
  const [drawResetSignal, setDrawResetSignal] = useState(0)

  const setPoint = useCallback(
    (lng: number, lat: number, searchLabel: string | null) => {
      setActiveTool('pin')
      onChange({ type: 'POINT', geometry: { type: 'Point', coordinates: [lng, lat] }, searchLabel })
    },
    [onChange],
  )

  const clearGeoreference = () => {
    onChange({ type: 'NONE', geometry: null, searchLabel: null })
    if (activeTool === 'draw') setDrawResetSignal((n) => n + 1)
  }

  const switchToPin = () => {
    if (georeference.type === 'POLYGON')
      onChange({ type: 'NONE', geometry: null, searchLabel: null })
    setActiveTool('pin')
  }

  const switchToDraw = () => {
    if (georeference.type === 'POINT') onChange({ type: 'NONE', geometry: null, searchLabel: null })
    setActiveTool('draw')
  }

  const handlePolygonComplete = useCallback(
    (ring: Position[]) => {
      onChange({
        type: 'POLYGON',
        geometry: { type: 'Polygon', coordinates: [ring as [number, number][]] },
        searchLabel: null,
      })
    },
    [onChange],
  )

  useMarkerSync(map, activeTool, georeference, setPoint)

  // A saved polygon shows via the static preview instead — Clear first to redraw.
  const isDrawingNew = activeTool === 'draw' && georeference.type === 'NONE'
  useDrawPolygon(map, isDrawingNew, drawResetSignal, handlePolygonComplete)

  usePolygonPreview(map, activeTool, georeference)

  useMapGeocoder(map, geocoderContainerRef, setPoint)

  const initialCenter: [number, number] =
    georeference.geometry?.type === 'Point'
      ? georeference.geometry.coordinates
      : clientLocation
        ? [clientLocation.longitude, clientLocation.latitude]
        : MapConfig.locations.newZealand.center
  const initialZoom =
    georeference.type === 'POINT'
      ? POINT_ZOOM_LEVEL
      : (clientLocation?.zoom ?? MapConfig.locations.newZealand.zoom)

  return (
    <div className="flex flex-col gap-4">
      <GeoreferenceHeader
        activeTool={activeTool}
        onSwitchToPin={switchToPin}
        onSwitchToDraw={switchToDraw}
        geocoderContainerRef={geocoderContainerRef}
      />

      <div className="relative h-80 rounded-md overflow-hidden border">
        <BaseMap
          initialView={{ center: initialCenter, zoom: initialZoom }}
          style={MapConfig.styles.lightWithHouseNumbers}
          onMapReady={setMap}
        />
      </div>

      <GeoreferenceStatusBar
        georeference={georeference}
        activeTool={activeTool}
        onClear={clearGeoreference}
      />
    </div>
  )
}
